/**
 * Live Terminal Take-Home Charges Breakdown & Position Cell Formatters.
 *
 * Computes realized closed intraday position P&L and charges (STT, Stamp Duty,
 * Exchange, SEBI, GST, Brokerage), and formats broker-sourced average/quantity table cells.
 */

export interface TakeHomeChargesBreakdown {
  stt: number;
  stampDuty: number;
  exchangeCharges: number;
  sebiCharges: number;
  miscCharges: number;
  brokerage: number;
  gst: number;
  total: number;
}

export interface TakeHomeResult {
  gross: number;
  charges: TakeHomeChargesBreakdown;
  net: number;
}

/**
 * Computes the aggregate gross P&L, itemized regulatory charges, and net take-home
 * across all closed intraday positions.
 */
export function computeTakeHomeCharges(
  rows: any[],
  computeChargesFn?: (
    side: string,
    buyPrice: number,
    sellPrice: number,
    qty: number,
  ) => Partial<TakeHomeChargesBreakdown>,
): TakeHomeResult {
  const charges: TakeHomeChargesBreakdown = {
    stt: 0,
    stampDuty: 0,
    exchangeCharges: 0,
    sebiCharges: 0,
    miscCharges: 0,
    brokerage: 0,
    gst: 0,
    total: 0,
  };
  let gross = 0;

  if (Array.isArray(rows) && computeChargesFn) {
    for (const p of rows) {
      if (!p) continue;
      // Today's CLOSED position: flat now (netqty 0) with both buy and sell legs
      if (parseInt(p.netqty || 0, 10) !== 0) continue;
      const dbq = parseFloat(p.daybuyqty || 0);
      const dsq = parseFloat(p.daysellqty || 0);
      if (dbq <= 0 || dsq <= 0) continue;

      const qty = Math.min(dbq, dsq);
      const buyPrice = parseFloat(p.daybuyavgprc || 0);
      const sellPrice = parseFloat(p.daysellavgprc || 0);

      const c = computeChargesFn("BUY", buyPrice, sellPrice, qty);
      gross += parseFloat(p.rpnl || 0);

      charges.stt += c.stt || 0;
      charges.stampDuty += c.stampDuty || 0;
      charges.exchangeCharges += c.exchangeCharges || 0;
      charges.sebiCharges += c.sebiCharges || 0;
      charges.miscCharges += c.miscCharges || 0;
      charges.brokerage += c.brokerage || 0;
      charges.gst += c.gst || 0;
      charges.total += c.total || 0;
    }
  }

  const net = gross - charges.total;
  return { gross, charges, net };
}

/**
 * Formats monetary amounts with exact 2 decimal places and Indian numbering grouping.
 */
export function formatTakeHomeMoney(v: number): string {
  return (
    "₹" +
    v.toLocaleString("en-IN", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    })
  );
}

/**
 * Formats the raw broker buy/sell average price cell.
 */
export function formatAvgPriceCell(
  rawRecord: any,
  which: "buy" | "sell",
): string {
  if (!rawRecord) return "—";
  const v =
    which === "buy"
      ? parseFloat(rawRecord.totbuyavgprc || rawRecord.daybuyavgprc || 0)
      : parseFloat(rawRecord.totsellavgprc || rawRecord.daysellavgprc || 0);
  return v ? v.toFixed(2) : "—";
}

/**
 * Formats the raw broker-sourced buy/sell quantity cell (day + carry-forward).
 */
export function formatQtyCell(
  rawRecord: any,
  which: "buy" | "sell",
): string {
  if (!rawRecord) return "—";
  const day = which === "buy" ? rawRecord.daybuyqty : rawRecord.daysellqty;
  const cf = which === "buy" ? rawRecord.cfbuyqty : rawRecord.cfsellqty;
  if (day == null && cf == null) return "—";
  return String((parseInt(day || 0, 10) || 0) + (parseInt(cf || 0, 10) || 0));
}
